import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

// ---- Date helpers (all local-time, ISO "YYYY-MM-DD" strings) ----
function toISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function parseISO(s) {
    if (!s) return null;
    const [y, m, d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
function addDays(d, n) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}
function addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function isSameDay(a, b) {
    return (
        a && b &&
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}
function todayLocal() {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

export function formatDateRangeLabel(startISO, endISO) {
    const s = parseISO(startISO);
    const e = parseISO(endISO);
    const locale = i18n.language;
    if (!s && !e) return i18n.t('ui-complex:dateRangePicker.selectRange');
    const full = { month: 'short', day: 'numeric', year: 'numeric' };
    const short = { month: 'short', day: 'numeric' };
    if (s && e) {
        if (isSameDay(s, e)) return s.toLocaleDateString(locale, full);
        if (s.getFullYear() === e.getFullYear()) {
            return `${s.toLocaleDateString(locale, short)} – ${e.toLocaleDateString(locale, full)}`;
        }
        return `${s.toLocaleDateString(locale, full)} – ${e.toLocaleDateString(locale, full)}`;
    }
    return (s || e).toLocaleDateString(locale, full);
}

// Semantic report time frames. Each preset carries a `type` (the backend
// `time_frame_type`); the computed `range` is only a calendar preview — on Apply
// the picker emits the `type` so the backend recomputes canonical dates for the
// active anchor. Arithmetic mirrors the backend (`_calculate_date_range`,
// Monday-based weeks; "yesterday" spans [yesterday, today]).
function buildDefaultPresets(t) {
    const today = todayLocal();
    const yesterday = addDays(today, -1);
    const mondayOffset = (today.getDay() + 6) % 7; // 0 = Monday … 6 = Sunday
    const thisMonday = addDays(today, -mondayOffset);
    const lastMonday = addDays(thisMonday, -7);
    const lastSunday = addDays(thisMonday, -1);
    const firstOfMonth = startOfMonth(today);
    const firstOfLastMonth = addMonths(today, -1); // already day=1
    const lastDayLastMonth = addDays(firstOfMonth, -1);
    return [
        { id: 'yesterday', type: 'yesterday', label: t('ui-complex:dateRangePicker.presets.yesterday'), range: [toISO(yesterday), toISO(today)] },
        { id: 'week_to_date', type: 'week_to_date', label: t('ui-complex:dateRangePicker.presets.weekToDate'), range: [toISO(thisMonday), toISO(today)] },
        { id: 'last_week', type: 'last_week', label: t('ui-complex:dateRangePicker.presets.lastWeek'), range: [toISO(lastMonday), toISO(lastSunday)] },
        { id: 'month_to_date', type: 'month_to_date', label: t('ui-complex:dateRangePicker.presets.monthToDate'), range: [toISO(firstOfMonth), toISO(today)] },
        { id: 'last_month', type: 'last_month', label: t('ui-complex:dateRangePicker.presets.lastMonth'), range: [toISO(firstOfLastMonth), toISO(lastDayLastMonth)] },
    ];
}

// 6-week grid (42 cells) for the month containing `monthDate`.
function monthCells(monthDate) {
    const first = startOfMonth(monthDate);
    const gridStart = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

function MonthCalendar({ monthDate, start, end, hover, onPick, onHover }) {
    useTranslation();
    const cells = monthCells(monthDate);
    const month = monthDate.getMonth();
    const monthLabel = monthDate.toLocaleDateString(i18n.language, { month: 'long' });
    const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
        new Date(2024, 0, 7 + i).toLocaleDateString(i18n.language, { weekday: 'short' }),
    );
    // Effective end for range highlighting: real end, else hovered day while picking.
    const effEnd = end || (start && hover ? hover : null);
    const lo = start && effEnd && start <= effEnd ? start : effEnd;
    const hi = start && effEnd && start <= effEnd ? effEnd : start;

    return (
        <div className="w-56">
            <div className="text-center text-sm font-semibold text-gray-700 mb-2">
                {monthLabel} {monthDate.getFullYear()}
            </div>
            <div className="grid grid-cols-7 gap-y-1">
                {weekdayLabels.map((w, i) => (
                    <div key={i} className="text-center text-[11px] font-medium text-gray-400 py-1">
                        {w}
                    </div>
                ))}
                {cells.map((day) => {
                    const inMonth = day.getMonth() === month;
                    const isStart = isSameDay(day, start);
                    const isEnd = isSameDay(day, end);
                    const inRange = lo && hi && day >= lo && day <= hi;
                    const isEndpoint = isStart || isEnd;

                    let cls = 'relative text-center text-sm py-1.5 cursor-pointer select-none transition-colors ';
                    if (!inMonth) {
                        cls += 'text-gray-300 hover:bg-gray-50 rounded-md ';
                    } else if (isEndpoint) {
                        cls += 'bg-primary-600 text-white font-semibold rounded-md ';
                    } else if (inRange) {
                        cls += 'bg-primary-100 text-primary-800 ';
                    } else {
                        cls += 'text-gray-700 hover:bg-gray-100 rounded-md ';
                    }
                    return (
                        <button
                            type="button"
                            key={toISO(day)}
                            className={cls}
                            onClick={() => onPick(day)}
                            onMouseEnter={() => onHover(day)}
                        >
                            {day.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Date-range picker: a trigger button that opens a popover with quick-select
 * presets (left) and a dual-month calendar (right). Props:
 *   startDate, endDate  — controlled ISO "YYYY-MM-DD" values
 *   onChange(start,end) — fires as the user finalizes a range (both endpoints)
 *   onApply(start,end)  — fires on the Apply button with the chosen range
 *   presets?            — override the default quick-select list
 *   disabled?
 */
export function DateRangePicker({
    startDate,
    endDate,
    onChange,
    onApply,
    presets,
    disabled = false,
    align = 'right',
}) {
    const [open, setOpen] = useState(false);
    // Draft range while the popover is open (committed on Apply).
    const [draftStart, setDraftStart] = useState(startDate || '');
    const [draftEnd, setDraftEnd] = useState(endDate || '');
    const [hover, setHover] = useState(null);
    const [viewMonth, setViewMonth] = useState(() =>
        startOfMonth(parseISO(startDate) || todayLocal()),
    );
    const rootRef = useRef(null);
    const { t } = useTranslation(['ui-complex', 'common']);

    const presetList = useMemo(() => presets || buildDefaultPresets(t), [presets, t]);

    // Re-seed the draft whenever the popover opens or the controlled value changes.
    useEffect(() => {
        if (!open) return;
        setDraftStart(startDate || '');
        setDraftEnd(endDate || '');
        setHover(null);
        setViewMonth(startOfMonth(parseISO(startDate) || todayLocal()));
    }, [open, startDate, endDate]);

    // Close on outside click / Escape.
    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const draftStartD = parseISO(draftStart);
    const draftEndD = parseISO(draftEnd);

    const handlePick = useCallback(
        (day) => {
            const iso = toISO(day);
            // No start yet, or a complete range already picked → start over.
            if (!draftStart || (draftStart && draftEnd)) {
                setDraftStart(iso);
                setDraftEnd('');
                return;
            }
            // Second click: order the two endpoints.
            if (iso < draftStart) {
                setDraftEnd(draftStart);
                setDraftStart(iso);
            } else {
                setDraftEnd(iso);
            }
        },
        [draftStart, draftEnd],
    );

    const applyPreset = useCallback((preset) => {
        const [s, e] = preset.range;
        setDraftStart(s);
        setDraftEnd(e);
        setHover(null);
        setViewMonth(startOfMonth(parseISO(s) || todayLocal()));
    }, []);

    const activePreset = useMemo(
        () =>
            presetList.find(
                (p) => p.range[0] === draftStart && p.range[1] === draftEnd,
            ) || null,
        [presetList, draftStart, draftEnd],
    );
    const activePresetId = activePreset?.id || null;

    const canApply = draftStart && draftEnd && draftStart <= draftEnd;

    // When the draft exactly matches a semantic preset, emit its `type` so the
    // consumer can sync by time frame (canonical dates); manual picks emit null.
    const handleApply = () => {
        if (!canApply) return;
        const type = activePreset?.type || null;
        if (onChange) onChange(draftStart, draftEnd, type);
        if (onApply) onApply(draftStart, draftEnd, type);
        setOpen(false);
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                    open
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
                <Calendar className="w-4 h-4" />
                <span className="font-medium">{formatDateRangeLabel(startDate, endDate)}</span>
            </button>

            {open && (
                <div
                    className={`absolute z-50 mt-2 flex rounded-xl border border-gray-200 bg-white shadow-xl ${
                        align === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    {/* Presets */}
                    <div className="w-40 flex-shrink-0 border-r border-gray-100 py-3">
                        {presetList.map((p) => (
                            <button
                                type="button"
                                key={p.id}
                                onClick={() => applyPreset(p)}
                                className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                                    activePresetId === p.id
                                        ? 'bg-primary-50 text-primary-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {/* Calendars */}
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                            <button
                                type="button"
                                onClick={() => setViewMonth((m) => addMonths(m, -1))}
                                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMonth((m) => addMonths(m, 1))}
                                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex gap-6" onMouseLeave={() => setHover(null)}>
                            <MonthCalendar
                                monthDate={viewMonth}
                                start={draftStartD}
                                end={draftEndD}
                                hover={hover}
                                onPick={handlePick}
                                onHover={setHover}
                            />
                            <MonthCalendar
                                monthDate={addMonths(viewMonth, 1)}
                                start={draftStartD}
                                end={draftEndD}
                                hover={hover}
                                onPick={handlePick}
                                onHover={setHover}
                            />
                        </div>

                        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                            <span className="text-sm text-gray-500">
                                {canApply
                                    ? formatDateRangeLabel(draftStart, draftEnd)
                                    : t('ui-complex:dateRangePicker.pickRange')}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100"
                                >
                                    {t('common:cancel')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleApply}
                                    disabled={!canApply || disabled}
                                    className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {t('ui-complex:dateRangePicker.apply')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DateRangePicker;
